import React, { useState, useRef, useEffect } from 'react';
import { Institution, searchInstitutions } from '../data/institutions';
import { Input } from './ui/input';
import { Button } from './ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from './ui/popover';
import { cn } from '@/lib/utils';
import { Search } from 'lucide-react';

interface InstitutionAutocompleteProps {
  value: string;
  onChange: (value: string) => void;
  existingInstitutions?: string[];
  placeholder?: string;
  className?: string;
}

// Match Select component: same trigger and content styling
const CONTROL_BG = 'bg-[var(--background-dark)]';
const TRIGGER_CLASS = cn(
  'flex h-11 w-full items-center justify-between rounded-lg border border-border px-3 py-2.5 text-[15px] font-normal',
  'text-foreground/70 ring-offset-background',
  'focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2',
  CONTROL_BG
);
const CONTENT_CLASS = cn(
  'relative z-50 max-h-[300px] overflow-hidden rounded-lg border border-border text-foreground/90 shadow-md',
  CONTROL_BG
);
/* Hover/focus via .institution-combobox-option in index.css (Tailwind hover: not applying in this context) */
const ITEM_CLASS = cn(
  'institution-combobox-option relative flex w-full cursor-pointer select-none items-center gap-3 rounded-md py-3 pl-3 pr-4 text-[15px] font-normal outline-none text-left'
);

const ChevronDownIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden><path d="m6 9 6 6 6-6" /></svg>
);

const INSTITUTION_DOMAIN: Record<string, string> = {
  'td': 'td.com',
  'rbc': 'rbc.com',
  'bmo': 'bmo.com',
  'scotiabank': 'scotiabank.com',
  'cibc': 'cibc.com',
  'national-bank': 'nbc.ca',
  'desjardins': 'desjardins.com',
};

function getInstitutionDomain(institution: Institution): string {
  return INSTITUTION_DOMAIN[institution.id] ?? `${institution.id.replace(/-/g, '')}.com`;
}

const InstitutionAutocomplete: React.FC<InstitutionAutocompleteProps> = ({
  value,
  onChange,
  existingInstitutions = [],
  placeholder = "Search institutions...",
  className = "",
}) => {
  const [open, setOpen] = useState(false);
  const [isAddingNew, setIsAddingNew] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  const suggestions = searchInstitutions(searchQuery, existingInstitutions);

  const handleSelectInstitution = (institution: Institution) => {
    onChange(institution.name);
    setOpen(false);
    setSearchQuery('');
  };

  const handleAddNew = () => {
    setOpen(false);
    setIsAddingNew(true);
    setSearchQuery('');
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  const handleAddNewSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      onChange(searchQuery.trim());
      setIsAddingNew(false);
      setSearchQuery('');
    }
  };

  const getCountryFlag = (country: string) => {
    switch (country) {
      case 'CA': return '🇨🇦';
      case 'US': return '🇺🇸';
      default: return '🌍';
    }
  };

  // Close on escape
  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open]);

  if (isAddingNew) {
    return (
      <form onSubmit={handleAddNewSubmit} className={cn('flex flex-col gap-2', className)}>
        <Input
          ref={inputRef}
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Enter institution name..."
          autoFocus
          className={CONTROL_BG}
        />
        <div className="flex gap-2">
          <Button type="submit" size="sm">Add Institution</Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => { setIsAddingNew(false); setSearchQuery(value); }}
          >
            Cancel
          </Button>
        </div>
      </form>
    );
  }

  return (
    <div className="institution-combobox">
    <Popover open={open} onOpenChange={setOpen} modal={false}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={cn(TRIGGER_CLASS, className)}
          aria-expanded={open}
          aria-haspopup="listbox"
        >
          <span className="line-clamp-1 text-left flex-1">
            {value || <span className="text-muted-foreground/80">{placeholder}</span>}
          </span>
          <span className="inline-flex h-4 w-4 shrink-0 opacity-50"><ChevronDownIcon /></span>
        </button>
      </PopoverTrigger>
      <PopoverContent
        disablePortal
        className={cn('w-[var(--radix-popover-trigger-width)] !p-0 !mt-0', CONTENT_CLASS)}
        align="start"
        style={{ pointerEvents: 'auto' }}
      >
        <div className="flex flex-col">
          <div className="flex items-center border-b border-border px-3 gap-2">
            <Search className="h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden />
            <input
              type="text"
              placeholder="Search institutions..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className={cn(
                'flex h-11 w-full rounded-md bg-transparent pt-2 pb-0.5 text-sm outline-none placeholder:text-muted-foreground/80',
                CONTROL_BG
              )}
              onKeyDown={(e) => e.stopPropagation()}
            />
          </div>
          <div
            ref={listRef}
            role="listbox"
            className="max-h-[260px] overflow-y-auto overflow-x-hidden px-2 pt-0.5 pb-2"
          >
            {suggestions.length === 0 ? (
              <div className="py-6 text-center text-sm text-muted-foreground">
                No institutions found for &ldquo;{searchQuery}&rdquo;
              </div>
            ) : (
              <>
                {suggestions.map((institution) => (
                  <div
                    key={institution.id}
                    role="option"
                    tabIndex={-1}
                    className={cn(ITEM_CLASS)}
                    onClick={() => handleSelectInstitution(institution)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleSelectInstitution(institution);
                      }
                    }}
                  >
                    <span className="w-5 shrink-0 flex items-center justify-center" aria-hidden>
                      <img
                        src={`https://www.google.com/s2/favicons?domain=${getInstitutionDomain(institution)}&sz=64`}
                        width={20}
                        height={20}
                        className="rounded-sm object-contain"
                        alt=""
                        onError={(e) => { e.currentTarget.src = '/fallback-bank-icon.svg'; }}
                      />
                    </span>
                    <span className="flex-1">{institution.name}</span>
                    <span className="shrink-0 text-sm" aria-hidden>
                      {getCountryFlag(institution.country)}
                    </span>
                  </div>
                ))}
                <div className="my-1 h-px bg-border" />
                <div
                  role="option"
                  tabIndex={-1}
                  className={cn(ITEM_CLASS, 'text-primary institution-combobox-option-add')}
                  onClick={handleAddNew}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleAddNew();
                    }
                  }}
                >
                  <span className="w-5 shrink-0 text-base leading-none" aria-hidden>➕</span>
                  <span>Add New Institution</span>
                </div>
              </>
            )}
          </div>
        </div>
      </PopoverContent>
    </Popover>
    </div>
  );
};

export default InstitutionAutocomplete;
